/**
 * Mapbox Geocoding API client
 *
 * Provides reverse geocoding (coordinates -> address) using Mapbox Geocoding API v5.
 * Uses the existing VITE_MAPBOX_TOKEN environment variable.
 */

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

export interface ReverseGeocodeResult {
  address_line1?: string
  city?: string
  state_province?: string
  postal_code?: string
  country?: string
}

/**
 * Reverse geocode coordinates to an address using Mapbox Geocoding API v5
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<ReverseGeocodeResult> {
  if (!MAPBOX_TOKEN) {
    console.warn('Mapbox token not configured, skipping reverse geocoding')
    return {}
  }

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&types=address,poi&limit=1`

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Geocoding failed: ${response.statusText}`)
  }

  const data = await response.json()
  if (!data.features || data.features.length === 0) {
    return {}
  }

  const feature = data.features[0]
  const context: Array<{ id: string; text: string }> = feature.context || []

  // Extract address components from Mapbox context array
  const getContextValue = (type: string) =>
    context.find((c) => c.id.startsWith(type))?.text

  return {
    address_line1: feature.address
      ? `${feature.address} ${feature.text}`
      : feature.place_name?.split(',')[0] || feature.text,
    city: getContextValue('place'),
    state_province: getContextValue('region'),
    postal_code: getContextValue('postcode'),
    country: getContextValue('country'),
  }
}
